import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { Loader } from "../components/atoms/Loader";
import ShowError from "../components/atoms/ShowError";
import { initZoho } from "../util/zohoUtil";
import { getShopifyStores } from "../APICalling/shopifyAdminApi";
import { getRelatedListSetting } from "../APICalling/settingApi";
import {
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  SHOPIFY_ORG_CONN,
  EXTENSION_IDENTIFIER,
} from "../config/serviceConstant";
// Per-module wiring lives in its own file — standard modules (Contacts /
// Accounts / Sales_Orders / Deals / Products) each have a separated entry,
// and custom modules resolve through a separate path driven by their Record
// View Setting's `resource`. See entityRegistry.js.
import { resolveEntityConfig } from "../components/PageComponent/RelatedList/entityRegistry";
import { EmptyState, Shell } from "../components/PageComponent/RelatedList/Common";

const ZOHO = window.ZOHO;

// Default field names used when the related-list setting is missing or
// doesn't override them. The source of truth is the settings API, which
// returns `{ IdField, shopUrlField }` inside settingSchema.
const DEFAULT_SHOPIFY_ID_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_ID_EXT`;
const DEFAULT_SHOPIFY_SHOP_URL_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_Shop_URL`;

export default function RelatedList() {
  const [loading, setLoading] = useState(true);
  const [extensionData, setExtensionData] = useState(null);

  // Entity from PageLoad — Zoho SDK detects the module, so the UI doesn't
  // need module tabs.
  const [entity, setEntity] = useState(null);
  const [entityId, setEntityId] = useState(null);
  const [recordData, setRecordData] = useState(null);

  // Shopify context derived from the Zoho record's extension-owned fields
  const [shopifyId, setShopifyId] = useState("");
  const [storeDomain, setStoreDomain] = useState("");

  // The record-view setting schema for this module. Standard modules only
  // need IdField/shopUrlField from it; CUSTOM modules also carry `resource`
  // ("customers" | "orders" | "products"), which resolveEntityConfig uses to
  // pick the renderer/fetcher.
  const [relatedListSchema, setRelatedListSchema] = useState(null);

  // True while the record + related-list setting are being fetched. The
  // shopifyId is still "" during this window, so guard the "Not linked" empty
  // state on this flag — otherwise it briefly flashes before the data arrives.
  const [resolving, setResolving] = useState(false);

  // Whatever the current entity's fetcher returns
  const [shopifyData, setShopifyData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");

  // Headers are only valid once we know the record's shop URL. Until
  // storeDomain is resolved from the record (or the legacy stores fallback),
  // return null so the downstream Shopify fetch is skipped — this prevents a
  // spurious first fetch with the wrong shop.
  const getHeaders = useCallback(() => {
    if (!extensionData || !storeDomain) return null;
    return {
      orgid: extensionData.orgId,
      connname: EXTENSION_IDENTIFIER,
      apikey: extensionData.apiKey,
      datacenterurl: extensionData.dataCenterUrl,
      datacenter: extensionData.dataCenter,
      [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(storeDomain),
    };
  }, [extensionData, storeDomain]);

  // ── Initialize Zoho & verify auth ──
  useEffect(() => {
    initZoho((error, data) => {
      if (error || !data) {
        setLoading(false);
        return;
      }
      setExtensionData(data);
      setEntity(data.entity || null);
      setEntityId(data.entityId || null);
      setLoading(false);
    });
  }, []);

  // ── Fetch Zoho record + related-list setting → derive Shopify ID + store URL ──
  // Field names come from the backend setting (settingType=relatedList,
  // moduleApiName=<entity>) so they can be reconfigured per module without a
  // UI change. Falls back to the extension-owned defaults if the setting is
  // absent, and to the first connected Shopify store when the record's shop
  // URL field is empty. Single effect → storeDomain is set exactly once and
  // the downstream Shopify fetch only fires once.
  useEffect(() => {
    if (!extensionData?.shopifyAuth || !extensionData?.zohoAuth) return;
    if (!entity || !entityId) return;

    setResolving(true);
    (async () => {
      try {
        const auth = {
          orgId: extensionData.orgId,
          apiKey: extensionData.apiKey,
          dataCenterUrl: extensionData.dataCenterUrl,
          shop: "",
        };

        const [setting, resp] = await Promise.all([
          getRelatedListSetting(auth, entity),
          ZOHO.CRM.API.getRecord({ Entity: entity, RecordID: entityId }),
        ]);

        const record = resp?.data?.[0] || null;
        setRecordData(record);

        const schema = setting?.settingSchema || {};
        setRelatedListSchema(schema);
        const idField = schema.IdField || DEFAULT_SHOPIFY_ID_FIELD;
        const shopField = schema.shopUrlField || DEFAULT_SHOPIFY_SHOP_URL_FIELD;

        setShopifyId(record?.[idField] || "");

        const recordShop = record?.[shopField];
        if (recordShop) {
          setStoreDomain(recordShop);
          return;
        }

        // Fallback: record has no shop URL — use the first connected store
        const tokensHeaders = {
          orgid: extensionData.orgId,
          connname: SHOPIFY_ORG_CONN,
          apikey: extensionData.apiKey,
          datacenterurl: extensionData.dataCenterUrl,
          datacenter: extensionData.dataCenter,
        };
        const stores = await getShopifyStores(tokensHeaders);
        if (stores?.length > 0) {
          setStoreDomain(
            stores[0]?.storeDomain || stores[0]?.domain || stores[0]?.name || ""
          );
        }
      } catch (err) {
        console.error("getRecord error:", err);
      } finally {
        setResolving(false);
      }
    })();
  }, [extensionData, entity, entityId]);

  // ── Fetch Shopify resource via the entity's resolved fetcher ──
  useEffect(() => {
    if (!extensionData?.shopifyAuth || !extensionData?.zohoAuth) return;
    if (!entity || !shopifyId) return;
    const registry = resolveEntityConfig(entity, relatedListSchema);
    if (!registry) return;
    const headers = getHeaders();
    if (!headers) return;

    (async () => {
      setDataLoading(true);
      setDataError("");
      try {
        const result = await registry.fetch(headers, shopifyId);
        setShopifyData(result);
      } catch (err) {
        console.error("fetchShopifyData error:", err);
        setShopifyData(null);
        setDataError(err?.message || "Failed to load Shopify data.");
      } finally {
        setDataLoading(false);
      }
    })();
  }, [extensionData, entity, shopifyId, relatedListSchema, getHeaders]);

  // ── Render guards ──

  if (loading) return <Loader />;

  if (!extensionData?.orgId || !extensionData?.apiKey || !extensionData?.dataCenterUrl) {
    return <ShowError />;
  }

  if (!extensionData?.shopifyAuth || !extensionData?.zohoAuth) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", p: 3 }}>
        <Box sx={{ textAlign: "center", color: "#71717a" }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>🔒</Box>
          <Box sx={{ fontSize: 16, fontWeight: 600, color: "#3f3f46", mb: 1 }}>
            Authentication Required
          </Box>
          <Box sx={{ fontSize: 14 }}>
            {!extensionData?.shopifyAuth && !extensionData?.zohoAuth
              ? "Please authenticate both Shopify and Zoho CRM in the Settings page."
              : !extensionData?.shopifyAuth
              ? "Please authenticate Shopify in the Settings page."
              : "Please authenticate Zoho CRM in the Settings page."}
          </Box>
        </Box>
      </Box>
    );
  }

  // While the Zoho record / related-list setting is still being fetched, show
  // the Shell in its loading state. This must run BEFORE the unsupported-
  // module check: a custom module's renderer is only known once its Record
  // View Setting (with `resource`) has loaded.
  if (resolving) {
    return <Shell storeDomain={storeDomain} loading={true} />;
  }

  // Unknown / unconfigured entity → friendly empty state instead of a blank
  // page. Standard modules resolve from the registry; custom modules resolve
  // from their Record View Setting's `resource`.
  const registry = resolveEntityConfig(entity, relatedListSchema);
  if (!registry) {
    return (
      <Shell storeDomain={storeDomain}>
        <EmptyState
          icon="❓"
          title="Module not configured"
          message={`The "${entity}" module isn't wired up for this widget yet. For a custom module, add a Record View for it in Settings → Record View Setting and choose which Shopify data (Customer / Order / Product) it should display.`}
        />
      </Shell>
    );
  }

  // Record has no Shopify ID linked → rich empty state with next steps
  if (!shopifyId) {
    return (
      <Shell storeDomain={storeDomain}>
        <EmptyState
          icon="🔗"
          tone="info"
          title="Not linked to Shopify"
          message="This record doesn't have a Shopify ID yet. Link it to see related Shopify data — customer profile, orders, product inventory — directly here."
        >
          <Box
            sx={{
              border: "1px solid #e4e4e7",
              borderRadius: "8px",
              bgcolor: "#fafafa",
              p: "14px 16px",
              textAlign: "left",
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", mb: "8px", textTransform: "uppercase", letterSpacing: 0.5 }}>
              How to link this record
            </Box>
            <Box component="ol" sx={{ m: 0, pl: "18px", display: "flex", flexDirection: "column", gap: "6px", fontSize: 14, color: "#334155" }}>
              <li>Run a sync from Shopify — the integration will fill the Shopify ID automatically.</li>
              <li>
                Or set the Shopify ID field on this {entity?.toLowerCase().replace(/_/g, " ").replace(/s$/, "") || "record"} manually to the matching Shopify resource ID.
              </li>
              <li>Make sure the Shopify Shop URL field points to the correct connected store.</li>
            </Box>
          </Box>
        </EmptyState>
      </Shell>
    );
  }

  // Fetcher returned a backend error (missing auth, shop mismatch, etc.)
  if (!dataLoading && dataError) {
    return (
      <Shell storeDomain={storeDomain}>
        <EmptyState
          icon="⚠️"
          tone="danger"
          title="Couldn't load Shopify data"
          message={dataError}
        />
      </Shell>
    );
  }

  const Entity = registry.Component;
  return (
    <Entity
      data={shopifyData}
      recordData={recordData}
      storeDomain={storeDomain}
      loading={dataLoading}
    />
  );
}
